import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Plus, Loader2, Calendar as CalendarIcon, Clock, Upload, X, FileText, Users, Search } from 'lucide-react';
import { leaveApi, employeeApi, uploadApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useManagerViewPermission } from '../../hooks/useManagerViewPermission';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';

interface LeaveRequestFormData {
  leave_policy_id: string;
  is_half_day: boolean;
  half_day_period?: 'morning' | 'afternoon';
  start_date: string;
  end_date: string;
  days_count: number;
  reason: string;
  attachment?: File | null;
}

interface DateRange {
  from: Date | undefined;
  to?: Date | undefined;
}

const MyLeavesNew: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<'my-leaves' | 'employee-leaves'>('my-leaves');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({ from: undefined, to: undefined });
  const [searchEmployee, setSearchEmployee] = useState('');
  const [selectedReporteeId, setSelectedReporteeId] = useState<string>('all');
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState<LeaveRequestFormData>({
    leave_policy_id: '',
    is_half_day: false,
    start_date: '',
    end_date: '',
    days_count: 1,
    reason: '',
    attachment: null,
  });

  // Get current user's employee ID from auth context
  const currentUserId = user?.id || '';
  const currentUserName = user ? `${user.first_name} ${user.last_name}` : '';
  const { isUserView, canViewEmployeeLeaves } = useManagerViewPermission();
  const isAdminOrManager = (user?.role === 'ADMIN' || user?.role === 'MANAGER' || user?.role === 'HR') && !isUserView && canViewEmployeeLeaves;

  // Fetch user's leave requests
  const { data: leaveRequests = [], isLoading: requestsLoading, refetch: refetchLeaveRequests } = useQuery({
    queryKey: ['myLeaveRequests', currentUserId],
    queryFn: () => leaveApi.requests.getAll({ employee_id: currentUserId }),
    enabled: !!currentUserId,
    refetchOnWindowFocus: true,
  });

  // Fetch employee leave overview (for admin/manager)
  const { data: employeeOverview = [], isLoading: overviewLoading } = useQuery({
    queryKey: ['employeeLeaveOverview'],
    queryFn: () => leaveApi.requests.getEmployeesOverview(),
    enabled: isAdminOrManager,
    refetchOnWindowFocus: true,
  });

  // Fetch leave policies
  const { data: policies = [] } = useQuery({
    queryKey: ['leavePolicies'],
    queryFn: () => leaveApi.policies.getAll(),
  });

  // Fetch employees for name resolution
  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeeApi.getAll({}),
  });

  // Build list of reportees from overview (for the dropdown)
  const reporteeList = useMemo(() => {
    return employeeOverview.filter((emp: any) => emp.employee_id !== currentUserId);
  }, [employeeOverview, currentUserId]);

  // Filter employee overview by dropdown selection AND search text
  const filteredEmployeeOverview = useMemo(() => {
    let list: any[] = employeeOverview;

    // If a specific reportee is selected, show only that one
    if (selectedReporteeId && selectedReporteeId !== 'all') {
      list = list.filter((emp: any) => emp.employee_id === selectedReporteeId);
    }

    // Further filter by search text
    if (searchEmployee.trim()) {
      const search = searchEmployee.toLowerCase();
      list = list.filter((emp: any) =>
        emp.employee_name?.toLowerCase().includes(search) ||
        emp.job_title?.toLowerCase().includes(search)
      );
    }

    return list;
  }, [employeeOverview, selectedReporteeId, searchEmployee]);

  // Auto-calculate days when dates change
  useEffect(() => {
    if (dateRange.from) {
      const startDate = format(dateRange.from, 'yyyy-MM-dd');
      const endDate = dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : startDate;

      let days = calculateDays(startDate, endDate);

      // Check if it's a single day and half day is selected
      if (startDate === endDate && formData.is_half_day) {
        days = 0.5;
      }

      setFormData(prev => ({
        ...prev,
        start_date: startDate,
        end_date: endDate,
        days_count: days
      }));
    }
  }, [dateRange, formData.is_half_day]);

  const createMutation = useMutation({
    mutationFn: leaveApi.requests.create,
    onSuccess: async () => {
      // Invalidate and refetch leave requests
      await queryClient.invalidateQueries({ queryKey: ['myLeaveRequests', currentUserId] });
      // Also invalidate team approvals if the user is a manager/admin
      await queryClient.invalidateQueries({ queryKey: ['teamLeaveRequests'] });
      // Also refetch explicitly to ensure we get the latest data
      await refetchLeaveRequests();

      setIsDialogOpen(false);
      resetForm();
      toast({ title: 'Leave request submitted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const handleSubmit = async () => {
    if (!formData.leave_policy_id || !dateRange.from) {
      toast({ title: 'Error', description: 'Please select leave type and date range', variant: 'destructive' });
      return;
    }

    if (formData.is_half_day && !formData.half_day_period) {
      toast({ title: 'Error', description: 'Please select half day period', variant: 'destructive' });
      return;
    }

    // Check if selected leave type requires medical certificate
    const selectedPolicy = policies.find((p: any) => p.id === formData.leave_policy_id);
    if (selectedPolicy?.leave_type?.toLowerCase().includes('medical') && !formData.attachment) {
      toast({
        title: 'Warning',
        description: 'Medical leave typically requires a certificate attachment',
        variant: 'destructive'
      });
      return;
    }

    try {
      let document_url: string | undefined;
      let document_filename: string | undefined;

      // Upload document if attached
      if (formData.attachment) {
        setIsUploading(true);
        try {
          const uploadResult = await uploadApi.uploadDocument(formData.attachment);
          document_url = uploadResult.url;
          document_filename = uploadResult.original_filename;
        } catch (uploadError: any) {
          toast({
            title: 'Upload Error',
            description: uploadError.message || 'Failed to upload document',
            variant: 'destructive'
          });
          setIsUploading(false);
          return;
        }
        setIsUploading(false);
      }

      createMutation.mutate({
        employee_id: currentUserId,
        employee_name: currentUserName,
        leave_policy_id: formData.leave_policy_id,
        start_date: formData.start_date,
        end_date: formData.end_date || formData.start_date,
        days_count: formData.days_count,
        reason: formData.reason,
        status: 'PENDING',
        is_half_day: formData.is_half_day,
        half_day_period: formData.half_day_period,
        document_url,
        document_filename,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit leave request',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      leave_policy_id: '',
      is_half_day: false,
      start_date: '',
      end_date: '',
      days_count: 1,
      reason: '',
      attachment: null,
    });
    setDateRange({ from: undefined, to: undefined });
  };

  const calculateDays = (start: string, end: string): number => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Error',
          description: 'File size must be less than 5MB',
          variant: 'destructive'
        });
        return;
      }
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Error',
          description: 'Only JPEG, PNG, and PDF files are allowed',
          variant: 'destructive'
        });
        return;
      }
      setFormData(prev => ({ ...prev, attachment: file }));
    }
  };

  const removeAttachment = () => {
    setFormData(prev => ({ ...prev, attachment: null }));
  };

  // Calculate leave balance based on policies and requests - memoized for performance and reactivity
  const leaveBalance = useMemo(() => {
    if (!policies || policies.length === 0) {
      return []; // Return empty array - will show N/A message
    }

    return policies.map((policy: any) => {
      // Count approved leaves for this policy type
      const approvedLeaves = leaveRequests.filter(
        (req: any) => req.leave_policy_id === policy.id && req.status === 'APPROVED'
      ).reduce((sum: number, req: any) => sum + (req.days_count || 1), 0);

      // Count pending leaves for this policy type - these should also reduce available balance
      const pendingLeaves = leaveRequests.filter(
        (req: any) => req.leave_policy_id === policy.id && req.status === 'PENDING'
      ).reduce((sum: number, req: any) => sum + (req.days_count || 1), 0);

      // Total committed leaves = approved + pending
      const totalCommitted = approvedLeaves + pendingLeaves;

      const total = policy.days_per_year || 0;
      // Available should account for both approved and pending leaves
      const available = Math.max(0, total - totalCommitted);

      return {
        type: policy.name || policy.leave_type,
        policyId: policy.id,
        available,
        used: approvedLeaves,
        pending: pendingLeaves,
        total
      };
    });
  }, [leaveRequests, policies]);

  // Get available leaves for selected policy
  const getAvailableLeaves = (policyId: string) => {
    const balance = leaveBalance.find(lb => lb.policyId === policyId);
    return balance || { available: 0, used: 0, pending: 0, total: 0 };
  };

  const getStatusBadge = (status: string) => {
    const statusColors = {
      PENDING: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400',
      APPROVED: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
      REJECTED: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
      CANCELLED: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400',
    };
    return (
      <Badge className={statusColors[status as keyof typeof statusColors] || ''} variant="outline">
        {status}
      </Badge>
    );
  };

  const getEmployeeInitials = (name: string) => {
    const parts = name.trim().split(' ');
    const first = parts[0]?.charAt(0) || '';
    const last = parts[parts.length - 1]?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  if (requestsLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading...</span>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto space-y-6 p-6 custom-scrollbar">
     

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#143A62] dark:text-slate-100">Leave Management</h1>
        </div>

        <Button 
          onClick={() => setIsDialogOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Request Leave
        </Button>
      </div>

      {/* Tabs - Show only if user is admin or manager */}
      {isAdminOrManager ? (
        <Tabs value={activeTab} onValueChange={(value: any) => setActiveTab(value)} className="w-full">
          <TabsList className="bg-white/50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 p-1 mb-8 w-fit shadow-sm backdrop-blur-sm">
            <TabsTrigger 
              value="my-leaves" 
              className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md shadow-none"
            >
              My Leaves
            </TabsTrigger>
            <TabsTrigger 
              value="employee-leaves"
              className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md shadow-none"
            >
              My Reportees Leaves
            </TabsTrigger>
          </TabsList>

          {/* My Leaves Tab */}
          <TabsContent value="my-leaves" className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            {/* Leave Balance Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {leaveBalance.length === 0 ? (
                <Card className="col-span-full border-none shadow-xl bg-slate-50/50 dark:bg-slate-900/50">
                  <CardContent className="py-12 text-center text-slate-500">
                    <p className="text-lg font-bold text-slate-800 dark:text-slate-200">No leave balance data</p>
                    <p className="text-sm mt-1">Contact your administrator to configure leave policies.</p>
                  </CardContent>
                </Card>
              ) : (
                leaveBalance.map((leave) => (
                  <Card key={leave.type} className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-6">
                        <div className="space-y-4">
                          <p className="text-sm font-bold text-slate-400 tracking-wide">{leave.type}</p>
                          <div className="space-y-1">
                            <h3 className="text-4xl font-bold text-slate-800 dark:text-white">{leave.available}</h3>
                            <p className="text-[12px] font-bold text-slate-400 tracking-wide">Available Days</p>
                          </div>
                        </div>
                        <div className={`p-3 rounded-2xl ${
                          leave.type.toLowerCase().includes('sick') 
                            ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-500' 
                            : 'bg-blue-50 dark:bg-blue-900/20 text-blue-500'
                        }`}>
                          <CalendarIcon className="h-6 w-6" />
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between text-[12px] font-bold text-slate-400 tracking-wide">
                          <div>
                            Used: <span className="text-slate-600 dark:text-slate-300 ml-1">{leave.used}</span>
                          </div>
                          <div>
                            Pending: <span className="text-orange-500 ml-1">{leave.pending}</span>
                          </div>
                          <div>
                            Total: <span className="text-slate-600 dark:text-slate-300 ml-1">{leave.total}</span>
                          </div>
                        </div>
                        <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-1.5 overflow-hidden">
                          <div
                            className={`h-1.5 rounded-full transition-all duration-700 shadow-sm ${
                              leave.type.toLowerCase().includes('sick') ? 'bg-rose-500' : 'bg-blue-500'
                            }`}
                            style={{ width: `${leave.total > 0 ? (leave.available / leave.total) * 100 : 0}%` }}
                          ></div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-bold text-[#143A62] dark:text-slate-100 flex items-center gap-2">
                  <span className="text-blue-500">⚡</span>
                  Leave History
                </h2>
              </div>

              <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent border-b-2 border-slate-50 dark:border-slate-800">
                        <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-6">Leave Type</TableHead>
                        <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Duration</TableHead>
                        <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 text-center">Days</TableHead>
                        <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Status</TableHead>
                        <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-6">Reason</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leaveRequests.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-20">
                            <div className="flex flex-col items-center">
                              <FileText className="h-12 w-12 text-slate-100 dark:text-slate-800 mb-4" />
                              <h3 className="text-lg font-bold text-slate-400">No leave history</h3>
                              <p className="text-sm text-slate-400 mt-1">Your approved and pending leaves will appear here.</p>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        leaveRequests.map((request: any) => {
                          const policy = policies.find((p: any) => p.id === request.leave_policy_id);
                          return (
                            <TableRow key={request.id} className="group border-b border-slate-50 dark:border-slate-800 last:border-0 hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors cursor-default">
                              <TableCell className="px-6 py-4">
                                <span className="font-bold text-[#143A62] dark:text-blue-400 group-hover:text-blue-600 transition-colors">
                                  {policy?.leave_type || policy?.name || 'N/A'}
                                </span>
                              </TableCell>
                              <TableCell className="py-4">
                                <div className="flex flex-col">
                                  <span className="text-sm font-bold text-slate-600 dark:text-slate-300">
                                    {format(new Date(request.start_date), 'MMM dd')} - {format(new Date(request.end_date), 'MMM dd')}
                                  </span>
                                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{format(new Date(request.start_date), 'yyyy')}</span>
                                </div>
                              </TableCell>
                              <TableCell className="py-4 text-center">
                                <div className="inline-flex flex-col items-center">
                                  <span className="text-sm font-black text-[#143A62] dark:text-white bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-lg">
                                    {request.days_count}
                                  </span>
                                  {request.is_half_day && <span className="text-[9px] font-bold text-blue-400 uppercase mt-1">Half Day</span>}
                                </div>
                              </TableCell>
                              <TableCell className="py-4">{getStatusBadge(request.status)}</TableCell>
                              <TableCell className="px-6 py-4">
                                <p className="text-sm font-bold text-slate-400 dark:text-slate-500 max-w-xs truncate italic">
                                  {request.reason || 'No reason provided'}
                                </p>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Employee Leaves Tab */}
          <TabsContent value="employee-leaves" className="space-y-8 pt-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
            {/* Search Bar + Reportee Dropdown */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Employee Dropdown Filter */}
              <div className="sm:w-72">
                <Select
                  value={selectedReporteeId}
                  onValueChange={(val) => setSelectedReporteeId(val)}
                >
                  <SelectTrigger className="h-12 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 rounded-xl shadow-sm font-bold text-sm">
                    <SelectValue placeholder="All Employees" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-slate-400" />
                        All Employees
                      </div>
                    </SelectItem>
                    {reporteeList.map((emp: any) => (
                      <SelectItem key={emp.employee_id} value={emp.employee_id}>
                        <div className="flex items-center gap-2">
                          <span className="font-bold">{emp.employee_name}</span>
                          {emp.job_title && (
                            <span className="text-xs text-slate-400 font-normal">· {emp.job_title}</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Search Input */}
              <div className="relative group flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                <Input
                  placeholder="Search by employee name or job title..."
                  value={searchEmployee}
                  onChange={(e) => setSearchEmployee(e.target.value)}
                  className="pl-11 h-12 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 rounded-xl shadow-sm font-bold text-sm"
                />
              </div>
            </div>

            {/* Employee Leave Overview */}
            {overviewLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
              </div>
            ) : filteredEmployeeOverview.length === 0 ? (
              <Card className="border-none shadow-xl bg-slate-50/50 dark:bg-slate-900/50">
                <CardContent className="py-20 text-center">
                  <div className="bg-white dark:bg-slate-800 h-20 w-20 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-slate-100 dark:border-slate-700 shadow-lg">
                    <Users className="h-10 w-10 text-slate-200 dark:text-slate-600" />
                  </div>
                  <p className="text-xl font-bold text-slate-800 dark:text-slate-200">No team data found</p>
                  <p className="text-sm text-slate-500 mt-1 max-w-xs mx-auto font-medium">Try adjusting your search criteria or check back later.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {filteredEmployeeOverview.map((empData: any) => (
                  <Card key={empData.employee_id} className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-1px] transition-all duration-300">
                    <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-5">
                          <div className="p-1 bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-100 dark:border-slate-700">
                            <Avatar className="h-14 w-14 rounded-xl">
                              <AvatarImage src={empData.avatar_url} />
                              <AvatarFallback className="bg-gradient-to-br from-blue-500 to-[#143A62] text-white font-bold text-lg">
                                {getEmployeeInitials(empData.employee_name)}
                              </AvatarFallback>
                            </Avatar>
                          </div>
                          <div>
                            <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">{empData.employee_name}</CardTitle>
                            <p className="text-sm text-blue-500 font-bold uppercase tracking-widest mt-0.5">{empData.job_title || 'N/A'}</p>
                          </div>
                        </div>
                        
                      </div>
                    </CardHeader>
                    <CardContent className="p-8">
                      {/* Leave Balances */}
                      <div className="mb-8">
                        <h4 className="text-s font-bold text-slate-400 tracking-widest mb-5 flex items-center">
                          <div className="w-1.5 h-4 bg-blue-500 rounded-full mr-2.5" />
                          Leave Balances
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {empData.leave_balances.map((balance: any, idx: number) => (
                            <div key={idx} className="border border-slate-100 dark:border-slate-800 rounded-2xl p-5 bg-white dark:bg-slate-900 shadow-md transition-all duration-300 hover:translate-y-[-1px]">
                              <div className="flex justify-between items-start mb-6">
                                <div className="space-y-4">
                                  <p className="text-sm font-bold text-slate-400 tracking-wide">{balance.policy_name}</p>
                                  <div className="space-y-1">
                                    <h3 className="text-3xl font-bold text-slate-800 dark:text-white">{balance.remaining}</h3>
                                    <p className="text-[12px] font-bold text-slate-400 tracking-wide">Available Days</p>
                                  </div>
                                </div>
                                <div className={`p-3 rounded-2xl ${
                                  balance.policy_name.toLowerCase().includes('sick') 
                                    ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-500' 
                                    : 'bg-blue-50 dark:bg-blue-900/20 text-blue-500'
                                }`}>
                                  <CalendarIcon className="h-5 w-5" />
                                </div>
                              </div>
                              
                              <div className="space-y-3">
                                <div className="flex items-center justify-between text-[12px] font-bold text-slate-400 tracking-wide">
                                  <div>
                                    Used: <span className="text-slate-600 dark:text-slate-300 ml-1">{balance.used}</span>
                                  </div>
                                  <div>
                                    Pending: <span className="text-orange-500 ml-1">{balance.pending}</span>
                                  </div>
                                  <div>
                                    Total: <span className="text-slate-600 dark:text-slate-300 ml-1">{balance.total}</span>
                                  </div>
                                </div>
                                <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-1.5 overflow-hidden">
                                  <div
                                    className={`h-1.5 rounded-full transition-all duration-700 shadow-sm ${
                                      balance.policy_name.toLowerCase().includes('sick') ? 'bg-rose-500' : 'bg-blue-500'
                                    }`}
                                    style={{ width: `${balance.total > 0 ? (balance.remaining / balance.total) * 100 : 0}%` }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Recent Leave Requests */}
                      {empData.recent_requests && empData.recent_requests.length > 0 && (
                        <div>
                          <h4 className="text-s font-bold text-slate-400 tracking-widest mb-4 flex items-center">
                            <div className="w-1.5 h-4 bg-teal-500 rounded-full mr-2.5" />
                            Recent Leave Requests
                          </h4>
                          <div className="grid grid-cols-1 gap-3">
                            {empData.recent_requests.slice(0, 2).map((request: any) => {
                              const policy = policies.find((p: any) => p.id === request.leave_policy_id);
                              return (
                                <div key={request.id} className="flex items-center justify-between p-4 border border-slate-50 dark:border-slate-800/50 rounded-2xl bg-white dark:bg-slate-800/50 hover:bg-slate-50 transition-colors">
                                  <div className="flex items-center space-x-4">
                                    <div className="h-10 w-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600">
                                      <CalendarIcon className="h-5 w-5" />
                                    </div>
                                    <div>
                                      <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{policy?.leave_type || 'N/A'}</p>
                                      <p className="text-[11px] text-slate-500 font-bold uppercase tracking-widest">
                                        {format(new Date(request.start_date), 'MMM dd')} - {format(new Date(request.end_date), 'MMM dd, yyyy')}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-6">
                                    <span className="text-sm font-bold text-[#143A62] dark:text-blue-400">{request.days_count} {request.days_count === 1 ? 'Day' : 'Days'}</span>
                                    {getStatusBadge(request.status)}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      ) : (
        /* Regular Employee View - No Tabs */
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
          {/* Leave Balance Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {leaveBalance.length === 0 ? (
              <Card className="col-span-full border-none shadow-xl bg-slate-50/50 dark:bg-slate-900/50">
                <CardContent className="py-20 text-center">
                  <div className="bg-white dark:bg-slate-800 h-20 w-20 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-slate-100 dark:border-slate-700 shadow-lg">
                    <CalendarIcon className="h-10 w-10 text-slate-200 dark:text-slate-600" />
                  </div>
                  <p className="text-xl font-bold text-slate-800 dark:text-slate-200">No balance info</p>
                  <p className="text-sm mt-1 text-slate-500 font-medium">Contact HR to configure your leave entitlements.</p>
                </CardContent>
              </Card>
            ) : (
              leaveBalance.map((leave) => (
              <Card key={leave.type} className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-6">
                    <div className="space-y-4">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">{leave.type}</p>
                      <div className="space-y-1">
                        <h3 className="text-4xl font-bold text-slate-800 dark:text-white">{leave.available}</h3>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Available Days</p>
                      </div>
                    </div>
                    <div className={`p-3 rounded-2xl ${
                      leave.type.toLowerCase().includes('sick') 
                        ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-500' 
                        : 'bg-blue-50 dark:bg-blue-900/20 text-blue-500'
                    }`}>
                      <CalendarIcon className="h-6 w-6" />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] font-bold text-slate-400 uppercase">
                        Used: <span className="text-slate-600 dark:text-slate-300 ml-1">{leave.used}</span>
                      </div>
                      <div className="text-[10px] font-bold text-slate-400 uppercase">
                        Total: <span className="text-slate-600 dark:text-slate-300 ml-1">{leave.total}</span>
                      </div>
                    </div>
                    <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-1.5 overflow-hidden">
                      <div
                        className={`h-1.5 rounded-full transition-all duration-700 shadow-sm ${
                          leave.type.toLowerCase().includes('sick') ? 'bg-rose-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${leave.total > 0 ? (leave.available / leave.total) * 100 : 0}%` }}
                      ></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
            )}
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold text-[#143A62] dark:text-slate-100 flex items-center gap-2">
                <span className="text-blue-500">⚡</span>
                Leave History
              </h2>
            </div>

            <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-b-2 border-slate-50 dark:border-slate-800">
                      <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-6">Leave Type</TableHead>
                      <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Duration</TableHead>
                      <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 text-center">Days</TableHead>
                      <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Status</TableHead>
                      <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-6">Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leaveRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-20">
                          <div className="flex flex-col items-center">
                            <FileText className="h-12 w-12 text-slate-100 dark:text-slate-800 mb-4" />
                            <h3 className="text-lg font-bold text-slate-400">No leave history</h3>
                            <p className="text-sm text-slate-400 mt-1">Your approved and pending leaves will appear here.</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      leaveRequests.map((request: any) => {
                        const policy = policies.find((p: any) => p.id === request.leave_policy_id);
                        return (
                          <TableRow key={request.id} className="group border-b border-slate-50 dark:border-slate-800 last:border-0 hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors cursor-default">
                            <TableCell className="px-6 py-4">
                              <span className="font-bold text-[#143A62] dark:text-blue-400 group-hover:text-blue-600 transition-colors">
                                {policy?.leave_type || policy?.name || 'N/A'}
                              </span>
                            </TableCell>
                            <TableCell className="py-4">
                              <div className="flex flex-col">
                                <span className="text-sm font-bold text-slate-600 dark:text-slate-300">
                                  {format(new Date(request.start_date), 'MMM dd')} - {format(new Date(request.end_date), 'MMM dd')}
                                </span>
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{format(new Date(request.start_date), 'yyyy')}</span>
                              </div>
                            </TableCell>
                            <TableCell className="py-4 text-center">
                              <div className="inline-flex flex-col items-center">
                                <span className="text-sm font-black text-[#143A62] dark:text-white bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-lg">
                                  {request.days_count}
                                </span>
                                {request.is_half_day && <span className="text-[9px] font-bold text-blue-400 uppercase mt-1">Half Day</span>}
                              </div>
                            </TableCell>
                            <TableCell className="py-4">{getStatusBadge(request.status)}</TableCell>
                            <TableCell className="px-6 py-4">
                              <p className="text-sm font-bold text-slate-400 dark:text-slate-500 max-w-xs truncate italic">
                                "{request.reason || 'No reason provided'}"
                              </p>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Request Leave Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="w-[min(96vw,800px)] max-w-[800px] max-h-[92vh] flex flex-col rounded-lg bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 transition-colors duration-300 gap-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 flex items-center gap-2">
              <span className="text-blue-500">⚡</span>
              Request Leave
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-8 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-800">
            <div className="space-y-5 max-w-xl mx-auto">
              {/* Leave Type */}
              <div className="space-y-1.5">
                <Label htmlFor="leave_type" className="text-slate-700 dark:text-slate-300 text-sm font-semibold block">
                  Leave Type <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.leave_policy_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, leave_policy_id: value }))}
                >
                  <SelectTrigger className="h-10 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 focus:ring-1 focus:ring-blue-500">
                    <SelectValue placeholder="Select leave type" />
                  </SelectTrigger>
                  <SelectContent>
                    {policies.map((policy: any) => (
                      <SelectItem key={policy.id} value={policy.id}>
                        {policy.leave_type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>                {/* Show available balance when leave type is selected */}
                {formData.leave_policy_id && (
                  <div className="mt-2 text-xs text-slate-600 dark:text-slate-400 bg-blue-50/50 dark:bg-blue-900/10 p-3 rounded-lg border border-blue-100 dark:border-blue-900/20">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-blue-800 dark:text-blue-300">Available: {getAvailableLeaves(formData.leave_policy_id).available} days</span>
                      <div className="flex items-center space-x-3 font-bold">
                        <span className="text-slate-500">Used: {getAvailableLeaves(formData.leave_policy_id).used}</span>
                        {getAvailableLeaves(formData.leave_policy_id).pending > 0 && (
                          <span className="text-orange-500">Pending: {getAvailableLeaves(formData.leave_policy_id).pending}</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

              </div>

              {/* Date Range Selection */}
              <div className="space-y-1.5">
                <Label className="text-slate-700 dark:text-slate-300 text-sm font-semibold block">
                  Date Range <span className="text-red-500">*</span>
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal h-10 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800",
                        !dateRange.from && "text-slate-400"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                      {dateRange.from ? (
                        dateRange.to ? (
                          <>
                            {format(dateRange.from, "MMM dd, yyyy")} - {format(dateRange.to, "MMM dd, yyyy")}
                          </>
                        ) : (
                          format(dateRange.from, "MMM dd, yyyy")
                        )
                      ) : (
                        "Pick a date or date range"
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 border-slate-200 dark:border-slate-800 rounded-lg shadow-lg" align="start">
                    <Calendar
                      mode="range"
                      selected={dateRange}
                      onSelect={(range) => range && setDateRange(range)}
                      numberOfMonths={2}
                      disabled={{ before: new Date(new Date().setHours(0,0,0,0)) }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Half Day & Days Count - Only show if dateRange is selected to prevent gap */}
              {dateRange.from && (
                <div className="grid grid-cols-2 gap-4">
                  {(!dateRange.to || format(dateRange.from, 'yyyy-MM-dd') === format(dateRange.to, 'yyyy-MM-dd')) && (
                    <div className="flex items-center justify-between p-3 border border-slate-200 dark:border-slate-700 rounded-lg">
                      <Label className="text-xs font-medium text-slate-600">Half Day</Label>
                      <Switch
                        checked={formData.is_half_day}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_half_day: checked }))}
                      />
                    </div>
                  )}
                  
                  <div className={cn(
                    "p-3 bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-lg flex items-center justify-between",
                    (!dateRange.to || format(dateRange.from, 'yyyy-MM-dd') === format(dateRange.to, 'yyyy-MM-dd')) ? "col-span-1" : "col-span-2"
                  )}>
                    <span className="text-xs font-medium text-blue-700 dark:text-blue-400">Total Days:</span>
                    <span className="font-bold text-blue-700 dark:text-blue-400">{formData.days_count}</span>
                  </div>
                </div>
              )}

              {/* Half Day Period Selection */}
              {formData.is_half_day && (
                <div className="space-y-1.5 animate-in fade-in slide-in-from-top-2 duration-200">
                  <Label className="text-slate-700 dark:text-slate-300 text-sm font-semibold block">Half Day Period <span className="text-red-500">*</span></Label>
                  <Select
                    value={formData.half_day_period}
                    onValueChange={(value: 'morning' | 'afternoon') =>
                      setFormData(prev => ({ ...prev, half_day_period: value }))
                    }
                  >
                    <SelectTrigger className="h-10 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100">
                      <SelectValue placeholder="Select period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="morning">Morning (9 AM - 1 PM)</SelectItem>
                      <SelectItem value="afternoon">Afternoon (1 PM - 5 PM)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Reason */}
              <div className="space-y-1.5">
                <Label className="text-slate-700 dark:text-slate-300 text-sm font-semibold block">Reason</Label>
                <Textarea
                  placeholder="Provide a reason for your leave request..."
                  value={formData.reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  rows={3}
                  className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 resize-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Attachment */}
              <div className="space-y-1.5">
                <Label className="text-slate-700 dark:text-slate-300 text-sm font-semibold block">
                  {(() => {
                    const selectedPolicy = policies.find((p: any) => p.id === formData.leave_policy_id);
                    const leaveType = selectedPolicy?.leave_type?.toLowerCase() || selectedPolicy?.name?.toLowerCase() || '';
                    const isSickLeave = leaveType.includes('sick') || leaveType.includes('medical');
                    return isSickLeave ? 'Medical Certificate' : 'Supporting Document';
                  })()}
                  {formData.leave_policy_id &&
                    policies.find((p: any) => p.id === formData.leave_policy_id)?.leave_type?.toLowerCase().includes('sick') &&
                    ' *'}
                </Label>

                {formData.attachment ? (
                  <div className="flex items-center justify-between p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <div className="flex items-center space-x-3 overflow-hidden">
                      <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      <span className="text-sm text-slate-700 dark:text-slate-300 truncate font-medium">{formData.attachment.name}</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={removeAttachment}
                      className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-28 border border-dashed border-slate-300 dark:border-slate-700 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6 text-center">
                        <Upload className="w-6 h-6 mb-2 text-slate-400" />
                        <p className="text-xs text-slate-500">
                          <span className="font-semibold">Click to upload</span> or drag and drop
                        </p>
                        <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-tight">PDF, PNG, JPG (max 5MB)</p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                      />
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0">
            <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={createMutation.isPending || isUploading}
            >
              {(createMutation.isPending || isUploading) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isUploading ? 'Uploading...' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyLeavesNew;
